<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <h3 class="mb-4">Application Automation Service Settings</h3>
      </v-col>

      <v-col cols="12" v-if="loadError">
        <v-alert type="error" dense>{{ loadError }}</v-alert>
      </v-col>

      <v-col cols="12">
        <v-form ref="formUrl" v-model="validUrl" lazy-validation>
          <!-- Automation Service URL -->
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="serviceUrl"
                :rules="[v => !!v || 'Service URL is required']"
                label="Automation Service URL"
                hint="Address where the Automation Service is reachable (e.g. http://192.168.1.42:8000)"
                persistent-hint
                placeholder="http://192.168.1.42:8000"
                outlined
                dense
                :loading="fetching"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-alert v-if="saveUrlSuccess" type="success" dense class="mb-3">
            Automation Service URL saved.
          </v-alert>
          <v-alert v-if="saveUrlError" type="error" dense class="mb-3">
            {{ saveUrlError }}
          </v-alert>

          <v-btn
            color="info"
            :disabled="!validUrl || savingUrl"
            :loading="savingUrl"
            @click="saveUrl"
            class="mr-3 mb-4"
          >
            SAVE URL
          </v-btn>

          <v-btn
            color="primary"
            outlined
            :loading="healthChecking"
            @click="checkHealth"
            class="mb-4"
          >
            Check Health
          </v-btn>

          <v-row align="center" class="ml-1 mb-2" v-if="healthStatus !== null">
            <v-col cols="auto" class="pa-0">
              <v-chip
                :color="healthStatus.ok ? 'success' : 'error'"
                dark
                small
              >
                <v-icon left small>{{ healthStatus.ok ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
                {{ healthStatus.ok ? `OK (${healthStatus.status_code})` : healthStatus.message }}
              </v-chip>
            </v-col>
            <v-col cols="auto" class="pa-0 ml-2" v-if="healthStatus.ok && healthStatus.data">
              <span class="caption grey--text">{{ JSON.stringify(healthStatus.data) }}</span>
            </v-col>
          </v-row>
        </v-form>

        <v-divider class="my-4" />

        <v-form ref="formModel" v-model="validModel" lazy-validation>
          <!-- LLM config fields (forwarded to the service itself) -->
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="baseUrl"
                label="Model Base URL"
                hint="Model URL passed to the LLM service config"
                persistent-hint
                placeholder="http://localhost:8000"
                outlined
                dense
                :loading="fetching"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="model"
                :rules="[v => !!v || 'Model is required']"
                label="Model"
                placeholder="gpt-4"
                outlined
                dense
                :loading="fetching"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="apiKey"
                label="API Key"
                hint="API key for LiteLLM (Bearer token)"
                persistent-hint
                placeholder="sk-..."
                outlined
                dense
                :loading="fetching"
                :append-icon="showApiKey ? 'mdi-eye-off' : 'mdi-eye'"
                :type="showApiKey ? 'text' : 'password'"
                @click:append="showApiKey = !showApiKey"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="githubToken"
                label="GitHub Token (optional)"
                hint="Personal access token for GitHub model access, if required"
                persistent-hint
                placeholder="ghp_xxxxxxxxxxxx"
                outlined
                dense
                :loading="fetching"
                :append-icon="showToken ? 'mdi-eye-off' : 'mdi-eye'"
                :type="showToken ? 'text' : 'password'"
                @click:append="showToken = !showToken"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-alert v-if="saveModelSuccess" type="success" dense class="mb-3">
            Model config saved.
          </v-alert>
          <v-alert v-if="saveModelError" type="error" dense class="mb-3">
            {{ saveModelError }}
          </v-alert>

          <v-btn
            color="info"
            :disabled="!validModel || savingModel"
            :loading="savingModel"
            @click="saveModel"
            class="mr-3"
          >
            Save Model Config
          </v-btn>

          <v-btn
            color="primary"
            outlined
            :loading="llmHealthChecking"
            @click="checkLlmHealth"
            class="mr-3"
          >
            CHECK LLM HEALTH
          </v-btn>

          <v-row align="center" class="ml-1 mb-2 mt-2" v-if="llmHealthStatus !== null">
            <v-col cols="auto" class="pa-0">
              <v-chip
                :color="(llmHealthStatus.ok && llmHealthStatus.model_available) ? 'success' : 'error'"
                dark
                small
              >
                <v-icon left small>{{ (llmHealthStatus.ok && llmHealthStatus.model_available) ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
                {{ (llmHealthStatus.ok && llmHealthStatus.model_available) ? 'Model available' : (llmHealthStatus.message || 'Failed') }}
              </v-chip>
            </v-col>
            <v-col cols="auto" class="pa-0 ml-2" v-if="llmHealthStatus.ok && llmHealthStatus.models && llmHealthStatus.models.length">
              <span class="caption grey--text">{{ llmHealthStatus.models.length }} models available</span>
            </v-col>
          </v-row>
        </v-form>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { apiService } from '../../service/api.service'

export default {
  name: 'AppAutomationSettings',
  data: () => ({
    valid: true,
    validUrl: true,
    validModel: true,
    serviceUrl: '',
    baseUrl: '',
    model: '',
    githubToken: '',
    apiKey: '',
    showToken: false,
    showApiKey: false,
    savingUrl: false,
    savingModel: false,
    saveUrlSuccess: false,
    saveUrlError: null,
    saveModelSuccess: false,
    saveModelError: null,
    loadError: null,
    healthChecking: false,
    healthStatus: null,
    llmHealthChecking: false,
    llmHealthStatus: null
  }),
  created () {
    this.fetchConfig()
  },
  methods: {
    async fetchConfig () {
      this.fetching = true
      this.loadError = null
      try {
        const [settingsRes, configRes] = await Promise.allSettled([
          apiService.get('/admin/application-automation-service'),
          apiService.get('/admin/llm/config')
        ])

        if (settingsRes.status === 'fulfilled') {
          const urlSetting = (settingsRes.value.data.settings || []).find(s => s.key === 'service_url')
          if (urlSetting) this.serviceUrl = urlSetting.value || ''
        }

        if (configRes.status === 'fulfilled') {
          this.baseUrl = configRes.value.data.llm_base_url || ''
          this.model = configRes.value.data.llm_model || ''
          this.apiKey = configRes.value.data.llm_api_key || ''
          this.githubToken = configRes.value.data.github_token || ''
        }
      } catch (err) {
        this.loadError = `Failed to load config: ${err.message}`
      } finally {
        this.fetching = false
      }
    },
    async saveUrl () {
      if (!this.$refs.formUrl.validate()) return
      this.savingUrl = true
      this.saveUrlSuccess = false
      this.saveUrlError = null
      try {
        await apiService.put('/admin/application-automation-service/service_url', { value: this.serviceUrl })
        this.saveUrlSuccess = true
        setTimeout(() => { this.saveUrlSuccess = false }, 3000)
      } catch (err) {
        this.saveUrlError = `Failed to save URL: ${err.message}`
      } finally {
        this.savingUrl = false
      }
    },
    async saveModel () {
      if (!this.$refs.formModel.validate()) return
      this.savingModel = true
      this.saveModelSuccess = false
      this.saveModelError = null
      try {
        const payload = { llm_base_url: this.baseUrl, llm_model: this.model, llm_api_key: this.apiKey }
        if (this.githubToken) payload.github_token = this.githubToken
        await apiService.put('/admin/llm/config', payload)
        this.saveModelSuccess = true
        setTimeout(() => { this.saveModelSuccess = false }, 3000)
      } catch (err) {
        this.saveModelError = `Failed to save model config: ${err.message}`
      } finally {
        this.savingModel = false
      }
    },
    async checkHealth () {
      this.healthChecking = true
      this.healthStatus = null
      try {
        const res = await apiService.get('/admin/llm/health')
        this.healthStatus = res.data
      } catch (err) {
        this.healthStatus = { ok: false, message: err.message }
      } finally {
        this.healthChecking = false
      }
    },
    async checkLlmHealth () {
      this.llmHealthChecking = true
      this.llmHealthStatus = null
      try {
        const res = await apiService.post('/admin/llm/test-connection', {
          base_url: this.baseUrl,
          model: this.model,
          api_key: this.apiKey
        })
        this.llmHealthStatus = res.data
      } catch (err) {
        this.llmHealthStatus = { ok: false, message: err.message }
      } finally {
        this.llmHealthChecking = false
      }
    }
  }
}
</script>
